/* =============================================================================
   Wo im Text steckt Mathe?

   Bevor der Text auf die Seiten verteilt wird, markiert diese Datei jede
   Formel mit zwei Zeichen aus dem privaten Unicode-Bereich (MATHE_AUF …
   MATHE_ZU). Die Seitenaufteilung in Home.js trennt den Text an Leerzeichen;
   damit eine Formel dabei nicht zerfällt, werden Leerzeichen innerhalb einer
   Formel durch MATHE_LEER ersetzt. Eine markierte Formel ist für den Rest der
   App also ein einziges, unteilbares „Wort“.

   Erkannt wird zweierlei:

   1. AUSDRÜCKLICH — alles zwischen $ … $, $$ … $$, \( … \) und \[ … \].
      Das ist auch genau das, was ChatGPT & Co. ausgeben, wenn man sie nach
      einem Rechenweg fragt; eingefügte KI-Antworten funktionieren dadurch
      ohne Nacharbeit. aligned-Umgebungen werden in Zeilen zerlegt.

   2. VON SELBST — ein Wort, das eindeutig nach Formel aussieht: 3/4, x^2,
      (x+1)/2, √(x+1), 2x+3=7, 5m/s², a≤b, 20°C, \frac{1}{2}. Bewusst vorsichtig:
      km/h, und/oder, Schüler*innen, 2024/25 und 12/2025 bleiben Text.

   Lange Formeln (ab UMBRUCH_AB Zeichen) werden an Leerzeichen der obersten
   Ebene in mehrere Stücke geteilt. So kann der Zeilenumbruch wie bei normalem Text dazwischen greifen,
   statt dass eine lange Gleichung über den Rand läuft.
   ========================================================================== */

export const MATHE_AUF = "\uE000";
export const MATHE_ZU = "\uE001";
export const MATHE_LEER = "\uE002";
/* Steht direkt hinter MATHE_AUF, wenn die Formel zu einer Rechnung mit
   Brüchen gehört (3/4 + 1/2 = 5/4). Dann liegen + und = auf derselben Linie
   wie die Bruchstriche — siehe layout.js. */
export const MATHE_BRUCHZEILE = "\uE003";

/* Zeichen, die keine Handschrift enthält und die deshalb immer über den
   Formelsatz laufen müssen — auch mitten im normalen Text. */
const SONDERZEICHEN =
  "√∛∜π²³¹⁰⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿ₀₁₂₃₄₅₆₇₈₉₊₋₌₍₎ₙₓ≤≥≠≈≡≅±∓·⋅×÷−∞→⇒⇔←↔⇐∠⊥∥∈∉⊂⊆∪∩∅ℝℕℤℚℂ°½¼¾⅓⅔⅕⅛′µ" +
  "αβγδεθλμπρστφωΔΩΣ△";
const SONDER_RE = new RegExp(`[${SONDERZEICHEN}]`);
const ASCII_RELATION_RE = /<=>|<->|<=|>=|!=|=\/=|->|=>/;

/* Bekannte Befehle, die außerhalb von $ … $ eine Formel auslösen. */
const LATEX_BEFEHL_RE =
  /\\(d?t?frac|sqrt|cdot|times|div|pm|mp|leq?|geq?|neq?|approx|pi|alpha|beta|gamma|delta|Delta|varepsilon|epsilon|lambda|mu|varphi|phi|omega|rho|sigma|tau|theta|infty|Rightarrow|Leftrightarrow|rightarrow|to|implies|iff|angle|perp|parallel|in|notin|cup|cap|emptyset|varnothing|mathbb|overline|vec|degree|circ|text|left|right|sin|cos|tan|log|ln|equiv|cong|subseteq?|triangle)(?![a-zA-Z])/;

/* Kurze Buchstabenfolgen, die in einer Formel als Wort stehen dürfen. */
const FUNKTIONSWOERTER = new Set([
  "sin", "cos", "tan", "ln", "log", "lg", "exp", "pi", "sqrt", "wurzel",
]);

export function enthaeltMathe(text) {
  return !!text && text.indexOf(MATHE_AUF) !== -1;
}

/* --- Hilfen -------------------------------------------------------------- */

/* Markdown aus KI-Antworten: **fett** und Überschriften-Rauten sind beim
   Abschreiben nie gemeint. Alles andere bleibt, wie es ist. */
function entferneMarkdown(text) {
  return text
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "");
}

function istLeer(ch) {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r";
}

/* Teilt eine Formel an Leerzeichen der obersten Ebene. Nicht geteilt wird
   in Klammern, nach einem Befehl, der noch Argumente erwartet (\frac {1}{2}),
   und rund um ^, _ und / (a / b ist ein Bruch). */
/* Ab dieser Länge darf eine Formel an Leerzeichen umbrechen. Kürzere bleiben
   ein Stück: LaTeX-Leerzeichen bedeuten nichts, die Abstände um + und =
   setzt layout.js selbst. Als Wortabstand gesetzt, wirkte 2x + 3 = 7 sonst
   wie auseinandergezogen. */
const UMBRUCH_AB = 40;

export function teileFormel(src) {
  if (src.replace(/\s+/g, " ").trim().length <= UMBRUCH_AB) {
    const ganz = src.replace(/\s+/g, " ").trim();
    return ganz ? [ganz] : [];
  }
  const teile = [];
  let tiefe = 0;
  let aktuell = "";
  const n = src.length;
  for (let i = 0; i < n; i++) {
    const ch = src[i];
    if (ch === "\\") {
      // \left( und \right) zählen wie Klammern, \{ \} ebenso
      const rest = src.slice(i);
      const m = rest.match(/^\\(left|right)\s*(\\[{}|]|[()[\]|.])/);
      if (m) {
        tiefe += m[1] === "left" ? 1 : -1;
        if (tiefe < 0) tiefe = 0;
        aktuell += m[0];
        i += m[0].length - 1;
        continue;
      }
      const b = rest.match(/^\\([a-zA-Z]+|.)/);
      if (b) {
        aktuell += b[0];
        i += b[0].length - 1;
        continue;
      }
    }
    if (ch === "(" || ch === "[" || ch === "{") tiefe++;
    else if (ch === ")" || ch === "]" || ch === "}") tiefe = Math.max(0, tiefe - 1);

    if (istLeer(ch) && tiefe === 0) {
      let j = i;
      while (j < n && istLeer(src[j])) j++;
      const vor = aktuell.trimEnd();
      const letztes = vor[vor.length - 1];
      const naechstes = src[j];
      const befehlOffen = /\\[a-zA-Z]+$/.test(vor);
      const kleben =
        befehlOffen ||
        "^_/".includes(letztes || "") ||
        "^_/{".includes(naechstes || "");
      if (kleben || !vor || j >= n) {
        aktuell += " ";
      } else {
        teile.push(vor);
        aktuell = "";
      }
      i = j - 1;
      continue;
    }
    aktuell += ch;
  }
  if (aktuell.trim()) teile.push(aktuell.trim());
  return teile;
}

function verpacke(stueck) {
  return MATHE_AUF + stueck.replace(/\s/g, MATHE_LEER) + MATHE_ZU;
}

/* Eine Formel (Inhalt ohne Begrenzer) -> markierter Text. Mehrzeilige
   Formeln (aligned, \\) werden zu mehreren Zeilen. */
function formelZuText(inhalt) {
  let s = inhalt
    .replace(/\\begin\{(aligned|align\*?|gather\*?|equation\*?|split|array)\}(\{[^}]*\})?/g, "")
    .replace(/\\end\{(aligned|align\*?|gather\*?|equation\*?|split|array)\}/g, "")
    .replace(/&/g, " ");
  const zeilen = s
    .split(/\\\\|\n\s*\n/)
    .map((z) => z.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  return zeilen.map((z) => teileFormel(z).map(verpacke).join(" ")).join("\n");
}

/* --- Erkennung im normalen Text ------------------------------------------ */

function istFormelartig(kern) {
  if (!/^[0-9A-Za-z+\-*/^_()[\]=<>.,|!%']+$/.test(kern)) return false;
  // Jahreszahlen und Daten bleiben Text: 2024/25, 12/2025, 1/2/2025
  if (/\d{4}\s*\/|\/\s*\d{4}/.test(kern)) return false;
  if (/\d+\/\d+\/\d+/.test(kern)) return false;

  // Buchstabenfolgen: in Formeln stehen einzelne Variablen. Längere Wörter
  // nur als Funktion (sin, ln …) oder als Einheit direkt vor ^ (cm^2).
  const woerter = kern.match(/[A-Za-z]+/g) || [];
  let pos = 0;
  for (const w of woerter) {
    const stelle = kern.indexOf(w, pos);
    pos = stelle + w.length;
    if (w.length <= 1 || FUNKTIONSWOERTER.has(w.toLowerCase())) continue;
    if (kern[stelle + w.length] === "^") continue;
    return false;
  }

  // Der Auslöser muss wirklich etwas verbinden: links eine Zahl, eine
  // Variable oder eine Klammer, rechts ebenso (oder ein Vorzeichen).
  const ausloeser = /([0-9A-Za-z)\]])\s*[/^_*=+<>]\s*([0-9A-Za-z(+-])/;
  if (!ausloeser.test(kern)) return false;

  // Zwei einzelne Buchstaben mit Schrägstrich (S/W, m/s) bleiben Text — wer
  // a/b als Bruch will, schreibt $a/b$.
  if (/^[A-Za-z]\/[A-Za-z]$/.test(kern)) return false;
  return true;
}

/* Ein Wort (ohne Leerzeichen) aus dem normalen Text. Gibt den Text mit
   markierter Formel zurück, oder das Wort unverändert. */
/* Umformungsschritt hinter einer Gleichung: | -3, | :2, | ·4, | ^2 */
const UMFORMUNG_RE = /^[-+:·*/^−]\s*[0-9A-Za-z(√]/;

function erkenneWort(wort, nachStrich = false) {
  if (wort === "|") return verpacke("|");
  if (nachStrich && UMFORMUNG_RE.test(wort)) {
    const m = wort.match(/^(.*?)([.,;!?]*)$/s);
    return teileFormel(m[1]).map(verpacke).join(" ") + m[2];
  }
  if (!wort) return wort;
  // Satzzeichen und Anführungszeichen am Rand gehören nicht zur Formel
  const m = wort.match(/^([„"“'(]*)(.*?)([.,;:!?"“”'»«]*)$/s);
  let vorne = m ? m[1] : "";
  let kern = m ? m[2] : wort;
  let hinten = m ? m[3] : "";

  // Eine öffnende Klammer vorne nur abtrennen, wenn sie hinten keinen
  // Partner hat — (3/4) bleibt als Ganzes eine Formel.
  if (vorne.includes("(")) {
    kern = vorne.replace(/[„"“']/g, "") + kern;
    vorne = vorne.replace(/\(/g, "");
  }
  if (!kern) return wort;
  const auf = (kern.match(/\(/g) || []).length;
  const zu = (kern.match(/\)/g) || []).length;
  if (zu > auf && kern.endsWith(")")) {
    hinten = ")" + hinten;
    kern = kern.slice(0, -1);
  }
  if (auf > zu && kern.startsWith("(")) {
    vorne = vorne + "(";
    kern = kern.slice(1);
  }
  if (!kern) return wort;

  // zusammengehaltene Klammern (s. klammerHaeltZusammen) ohne Leerzeichen prüfen
  const dicht = kern.replace(/\s+/g, "");
  const istMathe =
    LATEX_BEFEHL_RE.test(kern) ||
    SONDER_RE.test(dicht) ||
    ASCII_RELATION_RE.test(dicht) ||
    istFormelartig(dicht);

  if (!istMathe) return wort;
  return vorne + teileFormel(kern).map(verpacke).join(" ") + hinten;
}

/* Steht das Wort vor einer offenen runden Klammer, deren Inhalt Rechnung ist
   (√(a² + b²), 2^(n + 1), (x + 1)/(x - 1)), gehört das Leerzeichen noch zur
   Formel. Prosa in Klammern — (siehe Seite 3), (ca. 5 km) — bleibt getrennt:
   der Inhalt braucht ein Rechenzeichen und darf keine Wörter enthalten. */
function klammerHaeltZusammen(wort, text, i) {
  let offen = -1;
  let tiefe = 0;
  for (let k = 0; k < wort.length; k++) {
    if (wort[k] === "(") {
      if (tiefe === 0) offen = k;
      tiefe++;
    } else if (wort[k] === ")" && tiefe > 0) tiefe--;
  }
  if (tiefe === 0) return false;
  let ende = -1;
  for (let j = i; j < text.length && j - i < 60; j++) {
    const c = text[j];
    if (c === "\n") break;
    if (c === "(") tiefe++;
    else if (c === ")" && --tiefe === 0) {
      ende = j;
      break;
    }
  }
  if (ende === -1) return false;
  const inhalt = wort.slice(offen + 1) + text.slice(i, ende);
  if (!/^[0-9A-Za-z\s+\-−·*×/:^_.,=<>±√∛∜π()²³¹⁰-₟]+$/.test(inhalt)) return false;
  if (!/[+\-−·*×/:^=<>±√²³⁰-₟]/.test(inhalt)) return false;
  const laeufe = inhalt.match(/[A-Za-z]{2,}/g) || [];
  return laeufe.every((l) => FUNKTIONSWOERTER.has(l.toLowerCase()));
}

/* Normaler Text -> Text, in dem erkannte Formeln markiert sind. LaTeX-Befehle
   ohne $ dürfen Leerzeichen in geschweiften Klammern enthalten
   (\frac{a + b}{2}), deshalb wird hier nicht stumpf an Leerzeichen getrennt. */
function erkenneImText(text) {
  if (!text) return text;
  let aus = "";
  let wort = "";
  let tiefe = 0;
  let befehl = false;
  let nachStrich = false; // voriges Wort war ein einzelnes |
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "\\" && /[a-zA-Z]/.test(text[i + 1] || "")) befehl = true;
    if (befehl) {
      if (ch === "{") tiefe++;
      else if (ch === "}") tiefe = Math.max(0, tiefe - 1);
    }
    if (istLeer(ch) && tiefe === 0 && ch !== "\n" && klammerHaeltZusammen(wort, text, i)) {
      wort += ch;
      continue;
    }
    if (istLeer(ch) && tiefe === 0) {
      if (wort) {
        aus += erkenneWort(wort, nachStrich) + ch;
        nachStrich = wort === "|";
      } else aus += ch;
      if (ch === "\n") nachStrich = false;
      wort = "";
      befehl = false;
      continue;
    }
    wort += ch;
  }
  return aus + erkenneWort(wort, nachStrich);
}

/* --- Ausdrückliche Formeln ($ … $ usw.) ----------------------------------- */

/* Sucht das schließende $ nach der Pandoc-Regel: nach dem öffnenden $ kein
   Leerzeichen, vor dem schließenden keins und danach keine Ziffer. So bleibt
   „5$ und 10$“ ganz normaler Text. */
function findeDollarEnde(text, start) {
  for (let j = start; j < text.length; j++) {
    const ch = text[j];
    if (ch === "\n" && text[j + 1] === "\n") return -1;
    if (ch === "\\") {
      j++;
      continue;
    }
    if (ch === "$") {
      if (istLeer(text[j - 1])) continue;
      if (/[0-9]/.test(text[j + 1] || "")) continue;
      return j;
    }
  }
  return -1;
}

function zerlegeBegrenzer(text) {
  const teile = [];
  let rest = "";
  const flush = () => {
    if (rest) teile.push({ mathe: false, s: rest });
    rest = "";
  };
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const ch = text[i];
    const zwei = text.substr(i, 2);

    if (zwei === "\\[" || zwei === "\\(") {
      const ende = text.indexOf(zwei === "\\[" ? "\\]" : "\\)", i + 2);
      if (ende !== -1) {
        flush();
        teile.push({ mathe: true, block: zwei === "\\[", s: text.slice(i + 2, ende) });
        i = ende + 1;
        continue;
      }
    }
    if (zwei === "$$") {
      const ende = text.indexOf("$$", i + 2);
      if (ende !== -1) {
        flush();
        teile.push({ mathe: true, block: true, s: text.slice(i + 2, ende) });
        i = ende + 1;
        continue;
      }
    }
    if (ch === "$" && text[i - 1] !== "\\" && !istLeer(text[i + 1] || " ")) {
      const ende = findeDollarEnde(text, i + 1);
      if (ende !== -1) {
        flush();
        teile.push({ mathe: true, block: false, s: text.slice(i + 1, ende) });
        i = ende;
        continue;
      }
    }
    if (text.startsWith("\\begin{", i)) {
      const m = text.slice(i).match(/^\\begin\{(aligned|align\*?|gather\*?|equation\*?)\}/);
      if (m) {
        const endeTag = `\\end{${m[1]}}`;
        const ende = text.indexOf(endeTag, i);
        if (ende !== -1) {
          flush();
          teile.push({ mathe: true, block: true, s: text.slice(i, ende + endeTag.length) });
          i = ende + endeTag.length - 1;
          continue;
        }
      }
    }
    rest += ch;
  }
  flush();
  return teile;
}

/* --- Einstieg ------------------------------------------------------------ */

const merker = new Map();

/* Formeln, die in einer Zeile nur durch Leerzeichen getrennt aufeinander
   folgen, sind eine Rechnung. Kommt darin ein Bruch vor, bekommen alle Stücke
   die Markierung MATHE_BRUCHZEILE. */
const LAUF_RE = new RegExp(
  `${MATHE_AUF}[^${MATHE_ZU}]*${MATHE_ZU}(?:[ \\t]+${MATHE_AUF}[^${MATHE_ZU}]*${MATHE_ZU})*`,
  "g"
);
const BRUCH_RE = /\/|\\[dtc]?frac|[½¼¾⅓⅔⅕⅛]/;

function markiereBruchzeilen(text) {
  return text.replace(LAUF_RE, (lauf) =>
    BRUCH_RE.test(lauf) ? lauf.split(MATHE_AUF).join(MATHE_AUF + MATHE_BRUCHZEILE) : lauf
  );
}

export function bereiteMatheVor(text) {
  if (!text) return text;
  if (merker.has(text)) return merker.get(text);

  // Zeichen, die ChatGPT gern statt der Schulschreibweise nimmt und die keine
  // Handschrift hat: Gedankenstrich als Minus, ÷ als Geteilt, ∙ als Malpunkt.
  const schulisch = text
    .replace(/\r\n?/g, "\n")
    .replace(/[–—]/g, "-")
    .replace(/÷/g, ":")
    .replace(/∙/g, "·");
  const sauber = entferneMarkdown(schulisch);
  const teile = zerlegeBegrenzer(sauber);
  let aus = "";
  for (let k = 0; k < teile.length; k++) {
    const t = teile[k];
    if (!t.mathe) {
      aus += erkenneImText(t.s);
      continue;
    }
    const formel = formelZuText(t.s);
    if (!formel) continue;
    if (t.block) {
      // Abgesetzte Formeln stehen in einer eigenen Zeile.
      aus = aus.replace(/[ \t]+$/, "");
      if (aus && !aus.endsWith("\n")) aus += "\n";
      aus += formel;
      const danach = teile[k + 1];
      if (danach && !danach.mathe) {
        danach.s = danach.s.replace(/^[ \t]+/, "");
        if (danach.s && !danach.s.startsWith("\n")) aus += "\n";
      }
    } else {
      aus += formel;
    }
  }

  aus = markiereBruchzeilen(aus);
  if (merker.size > 50) merker.clear();
  merker.set(text, aus);
  return aus;
}

/* Ein Wort aus der Seitenaufteilung -> Einheiten zum Zeichnen.
   Normale Zeichen einzeln, jede Formel als Ganzes. `laenge` ist die Anzahl
   Zeichen im Text — damit zählt der Zeichenindex (Zufallsvariation) weiter
   genauso wie ohne Formeln. */
export function zerlegeInEinheiten(token) {
  if (!enthaeltMathe(token)) {
    return Array.from(token, (ch) => ({ mathe: false, ch, laenge: ch.length }));
  }
  const einheiten = [];
  let i = 0;
  while (i < token.length) {
    if (token[i] === MATHE_AUF) {
      const ende = token.indexOf(MATHE_ZU, i + 1);
      const stop = ende === -1 ? token.length : ende;
      const quelle = token.slice(i + 1, stop).split(MATHE_LEER).join(" ");
      einheiten.push({ mathe: true, quelle, laenge: stop + 1 - i });
      i = stop + 1;
      continue;
    }
    const cp = token.codePointAt(i);
    const ch = String.fromCodePoint(cp);
    einheiten.push({ mathe: false, ch, laenge: ch.length });
    i += ch.length;
  }
  return einheiten;
}
